import { useCallback, useEffect, useState } from 'react';
import { api, tokenStore, registerUnauthorizedHandler } from '@/lib/api';
import type { User } from '@/lib/types';

interface LoginResponse { token: string; user: User }
interface MeResponse { user: User }

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Boot: if we have a token, fetch /me to validate + load user.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!tokenStore.get()) { setLoading(false); return; }
      try {
        const data = await api<MeResponse>('/api/auth/me');
        if (!cancelled) setUser(data.user);
      } catch {
        if (!cancelled) tokenStore.clear();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Global 401 → logout
  useEffect(() => {
    registerUnauthorizedHandler(() => setUser(null));
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const data = await api<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: { username, password },
    });
    tokenStore.set(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api('/api/auth/logout', { method: 'POST' }); } catch { /* ignore */ }
    tokenStore.clear();
    setUser(null);
  }, []);

  const isSuperadmin = user?.role === 'superadmin';
  const isAdminRole  = user?.role === 'superadmin' || user?.role === 'admin';
  const isViewer     = user?.role === 'viewer';

  return { user, loading, login, logout, isSuperadmin, isAdminRole, isViewer };
}
